import React, { useEffect, useState } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { AppButton } from '../ui/AppButton';
import { buildingsAPI } from '../../services/api';

const offerSchema = z.object({
  dataType: z.string().min(2, 'Data type is required'),
  buildingReference: z.string().uuid('Please select a building'),
  dataDescription: z.string().min(5, 'Description is required').max(500),
  priceEth: z.coerce.number().positive('Price must be positive').max(10000, 'Price seems too high — enter value in ETH, not wei'),
  validityDays: z.coerce.number().int().positive().max(365),
  dataHashRef: z.string().min(3).max(500),
  country: z.string().max(56).optional(),
  typology: z.string().max(56).optional(),
});

/**
 * Validated form to publish an offer and trigger PoE + marketplace listing.
 */
export const PublishOfferForm = ({ onSubmit, submitting }) => {
  const [buildings, setBuildings] = useState([]);
  const [buildingsLoading, setBuildingsLoading] = useState(true);

  useEffect(() => {
    setBuildingsLoading(true);
    buildingsAPI
      .getBuildings()
      .then((data) => setBuildings(data || []))
      .catch(() => {
        setBuildings([]);
        toast.error('Could not load buildings — enter UUID manually');
      })
      .finally(() => setBuildingsLoading(false));
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(offerSchema),
    defaultValues: {
      dataType: 'EPC',
      buildingReference: '',
      dataDescription: '',
      priceEth: 0.01,
      validityDays: 30,
      dataHashRef: '',
      country: '',
      typology: '',
    },
  });

  const submit = async (values) => {
    await onSubmit(values);
    reset({
      ...values,
      dataDescription: '',
      dataHashRef: '',
    });
  };

  return (
    <section className="panel-card" aria-labelledby="publish-offer-title">
      <div className="panel-header">
        <h2 id="publish-offer-title">Publish Data Offer</h2>
      </div>

      <form onSubmit={handleSubmit(submit)} className="form-grid publish-offer-form">
        <label className="field">
          Data Type
          <select {...register('dataType')} aria-invalid={Boolean(errors.dataType)}>
            <option value="EPC">EPC</option>
            <option value="sensor_stream">Sensor Stream</option>
            <option value="renovation_record">Renovation Record</option>
            <option value="ai_service_access">AI Service Access</option>
          </select>
          {errors.dataType && <span className="field-error">{errors.dataType.message}</span>}
        </label>

        <label className="field">
          Building
          {!buildingsLoading && buildings.length > 0 ? (
            <select
              {...register('buildingReference')}
              aria-invalid={Boolean(errors.buildingReference)}
            >
              <option value="">— Select a building —</option>
              {buildings.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name ? `${b.name} (${b.id.slice(0, 8)}…)` : b.id}
                </option>
              ))}
            </select>
          ) : (
            <input
              {...register('buildingReference')}
              aria-invalid={Boolean(errors.buildingReference)}
              placeholder={buildingsLoading ? 'Loading buildings…' : 'Building UUID'}
              disabled={buildingsLoading}
            />
          )}
          {errors.buildingReference && (
            <span className="field-error">{errors.buildingReference.message}</span>
          )}
        </label>

        <label className="field field--wide">
          Data Description
          <textarea rows={3} {...register('dataDescription')} />
          {errors.dataDescription && (
            <span className="field-error">{errors.dataDescription.message}</span>
          )}
        </label>

        <label className="field">
          Price (ETH)
          <input
            type="number"
            step="0.0001"
            min="0"
            max="10000"
            placeholder="e.g. 0.05"
            {...register('priceEth')}
          />
          {errors.priceEth
            ? <span className="field-error">{errors.priceEth.message}</span>
            : <span className="field-hint">Enter amount in ETH, not wei</span>
          }
        </label>

        <label className="field">
          Validity Period (days)
          <input type="number" min="1" max="365" {...register('validityDays')} />
          {errors.validityDays && (
            <span className="field-error">{errors.validityDays.message}</span>
          )}
        </label>

        <label className="field field--wide">
          IPFS CID / Off-chain URL
          <input {...register('dataHashRef')} />
          {errors.dataHashRef && <span className="field-error">{errors.dataHashRef.message}</span>}
        </label>

        <label className="field">
          Country
          <input {...register('country')} />
        </label>

        <label className="field">
          Building Typology
          <input {...register('typology')} />
        </label>

        <AppButton type="submit" variant="primary" loading={submitting} className="publish-offer-submit">
          {submitting ? 'Publishing…' : 'Publish Offer'}
        </AppButton>
      </form>
    </section>
  );
};
